// WP-3.8 (M20 RE-1/3/4, S-27/28): report queries + the export mapping. Per exportTable.ts's
// own header comment, the CALLER (here) builds the `TabularData` — the engine returns raw
// numbers only.
import type { CurrencyCode, ReportFamily, YearBasis } from '@pfos/engine';
import { fromMinor, toDecimalString, type TabularData } from '@pfos/engine';
import {
  reportGalleryResponseSchema,
  reportResultResponseSchema,
  type ReportGalleryResponse,
  type ReportResultResponse,
} from '@pfos/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api';

export const REPORTS_KEY = ['reports'] as const;

export function useReportGallery(): UseQueryResult<ReportGalleryResponse> {
  return useQuery({
    queryKey: [...REPORTS_KEY, 'gallery'],
    queryFn: async () => reportGalleryResponseSchema.parse(await api.get('/api/v1/reports')),
  });
}

export interface ReportFilters {
  currency: CurrencyCode;
  yearBasis?: YearBasis;
  asOf?: string;
}

export function useReport(
  family: ReportFamily,
  filters: ReportFilters,
): UseQueryResult<ReportResultResponse> {
  const params = new URLSearchParams({ currency: filters.currency });
  if (filters.yearBasis !== undefined) params.set('yearBasis', filters.yearBasis);
  if (filters.asOf !== undefined) params.set('asOf', filters.asOf);
  return useQuery({
    queryKey: [...REPORTS_KEY, family, filters],
    queryFn: async () =>
      reportResultResponseSchema.parse(
        await api.get(`/api/v1/reports/${family}?${params.toString()}`),
      ),
  });
}

export interface ReportColumn {
  key: string;
  label: string;
}

// RE-1's own field list per family — the ONE place a family's shape is named on the web side.
export const REPORT_COLUMNS: Record<ReportFamily, ReportColumn[]> = {
  income: [
    { key: 'client', label: 'Client' },
    { key: 'project', label: 'Project' },
    { key: 'earnedMinor', label: 'Earned' },
    { key: 'receivedMinor', label: 'Received' },
    { key: 'outstandingMinor', label: 'Outstanding' },
  ],
  expenses: [
    { key: 'category', label: 'Category' },
    { key: 'amountMinor', label: 'Amount' },
  ],
  fees: [
    { key: 'type', label: 'Type' },
    { key: 'amountMinor', label: 'Amount' },
  ],
  cashflow: [
    { key: 'month', label: 'Month' },
    { key: 'incomeMinor', label: 'Income' },
    { key: 'expensesMinor', label: 'Expenses' },
    { key: 'transfersMinor', label: 'Transfers' },
    { key: 'netMinor', label: 'Net' },
  ],
  outstanding: [
    { key: 'party', label: 'Party' },
    { key: 'kind', label: 'Kind' },
    { key: 'remainingMinor', label: 'Remaining' },
    { key: 'overdue', label: 'Overdue' },
  ],
  currency: [
    { key: 'currency', label: 'Currency' },
    { key: 'grossReceivedMinor', label: 'Gross received' },
  ],
  thirdParty: [
    { key: 'party', label: 'Party' },
    { key: 'receivedMinor', label: 'Received' },
    { key: 'returnedMinor', label: 'Returned' },
    { key: 'outstandingMinor', label: 'Outstanding' },
  ],
  aging: [
    { key: 'bucket', label: 'Bucket' },
    { key: 'amountMinor', label: 'Amount' },
    { key: 'count', label: 'Count' },
  ],
  feesByChannel: [
    { key: 'channel', label: 'Channel' },
    { key: 'grossMinor', label: 'Gross received' },
    { key: 'feesMinor', label: 'Fees' },
    { key: 'feePercent', label: 'Fee %' },
  ],
};

// The single column each family's minimal S-27/28 "chart" bars against.
export const REPORT_PRIMARY_COLUMN: Record<ReportFamily, string> = {
  income: 'receivedMinor',
  expenses: 'amountMinor',
  fees: 'amountMinor',
  cashflow: 'netMinor',
  outstanding: 'remainingMinor',
  currency: 'grossReceivedMinor',
  thirdParty: 'outstandingMinor',
  aging: 'amountMinor',
  feesByChannel: 'feePercent', // the report's own point — which channel eats the most, as a %
};

export function isMoneyColumn(key: string): boolean {
  return key.endsWith('Minor');
}

// WP-5.4: `feePercent` is a fraction (0.024 = 2.4%), the same convention `marginPercent`
// (WP-3.6) already uses — displayed to one decimal place, not the raw fraction.
export function isPercentColumn(key: string): boolean {
  return key.endsWith('Percent');
}

export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

const YEAR_BASIS_LABEL: Record<YearBasis, string> = { calendar: 'Jan–Dec', fiscal: 'Jul–Jun' };

/** RE-4: every export states its period, year-basis, and valuation-basis footnote. */
export function buildReportExport(
  report: ReportResultResponse,
  columns: ReportColumn[],
): TabularData {
  return {
    columns: columns.map((c) => ({
      key: c.key,
      label: isMoneyColumn(c.key) ? `${c.label} (${report.currency})` : c.label,
    })),
    rows: report.rows.map((row) => {
      const out: Record<string, string | number> = {};
      for (const c of columns) {
        const value = row[c.key];
        if (isMoneyColumn(c.key) && typeof value === 'number') {
          out[c.key] = Number(toDecimalString(fromMinor(report.currency, value)));
        } else {
          out[c.key] = value ?? '';
        }
      }
      return out;
    }),
    footnotes: [
      `Period: ${report.period.from.slice(0, 10)} to ${report.period.to.slice(0, 10)} (${YEAR_BASIS_LABEL[report.yearBasis]}).`,
      'Valuation basis: actual recorded amounts — no indicative FX conversion applied (RE-4).',
    ],
  };
}
